import {
  StyledBox,
  CustomToolbar,
  ZebraDataGrid,
  GridEditDateCell,
  EditToolbarProducts,
  UserAutocompleteEditCell,
} from 'components';
import dayjs from 'dayjs';
import {
  GridRowId,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowsProp,
  GridColTypeDef,
  GridEventListener,
  GridRowModesModel,
  GridValidRowModel,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowEditStopReasons,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-premium';
import { useIntl } from 'react-intl';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { useTheme, alpha, Chip } from '@mui/material';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM } from 'enums';
import { Dispatch, SetStateAction, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { EnvironmentType, Project, User } from 'library/types';
import { renderDataGridLocaleText, renderFormattedDate } from 'utils';

type ProductsEditTableProps = {
  rows: GridRowsProp;
  setRows: Dispatch<SetStateAction<readonly GridValidRowModel[]>>;
  rowModesModel: GridRowModesModel;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
  products: Project[];
  users: User[];
  environmentTypes: EnvironmentType[];
  editMode: boolean;
  completedProjectIds: number[];
};

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    const v = params.value;

    const dayjsValue = v instanceof Date ? dayjs(v) : v ? dayjs(v) : null;

    return <GridEditDateCell {...params} value={dayjsValue} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponentProps: { showTime: false },
  })),
};

const ProductsEditTable = ({
  rows,
  setRows,
  rowModesModel,
  setRowModesModel,
  products,
  users,
  environmentTypes,
  editMode,
  completedProjectIds,
}: ProductsEditTableProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();

  const priorityOptions = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.nonUrgent', defaultMessage: 'Düşük' }),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.urgent', defaultMessage: 'Orta' }),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.veryUrgent', defaultMessage: 'Yüksek' }),
      },
    ],
    [intl]
  );

  const isCompletedProduct = (row: GridValidRowModel) => {
    const productId = Number(row.projectId);
    return Number.isFinite(productId) && completedProjectIds.includes(productId);
  };

  // dynamic variables
  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'projectId',
      headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
      minWidth: 200,
      editable: editMode,
      type: 'singleSelect',
      valueOptions: () => products.map((product) => ({ value: product.id, label: product.title })),
    },
    {
      field: 'environmentTypeId',
      headerName: intl.formatMessage({ id: 'project.table.environmentType', defaultMessage: 'Sunucu Tipi' }),
      minWidth: 150,
      editable: editMode,
      type: 'singleSelect',
      valueOptions: () =>
        environmentTypes.map((environmentType) => ({ value: environmentType.id, label: environmentType.title })),
    },
    {
      field: 'liveDate',
      ...dateColumnType,
      valueGetter: (value) => {
        if (!value) return null;
        return new Date(value);
      },
      valueParser: (value) => {
        if (!value) return null;
        return new Date(value);
      },
      valueFormatter: (value) => {
        return value ? renderFormattedDate(value) : '';
      },
      headerName: intl.formatMessage({ id: 'project.table.liveDate', defaultMessage: 'Canlı Geçiş' }),
      minWidth: 150,
      editable: editMode,
    },
    {
      field: 'priority',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.priority',
        defaultMessage: 'Öncelik',
      }),
      headerAlign: 'center',
      align: 'center',
      type: 'singleSelect',
      editable: editMode,
      valueOptions: priorityOptions.map((x) => x.value),
      getOptionLabel: (value) => priorityOptions.find((x) => x.value === value)?.label ?? String(value),
      valueFormatter: (value) => priorityOptions.find((x) => x.value === value)?.label ?? '',
      renderCell: (params) => {
        if (!params.value) return null;
        const priority = params.value;
        const colorKey =
          priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
            ? 'success'
            : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
              ? 'warning'
              : 'error';

        return (
          <Chip
            size="small"
            label={priorityOptions.find((x) => x.value === priority)?.label ?? ''}
            sx={{
              fontWeight: 600,
              bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
              color: theme.palette[colorKey].main,
              border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
            }}
          />
        );
      },
      minWidth: 160,
    },
    {
      field: 'isOnHold',
      headerName: intl.formatMessage({
        id: 'project.table.isOnHold',
        defaultMessage: 'Beklemede',
      }),
      type: 'boolean',
      editable: editMode,
      minWidth: 120,
    },
    {
      field: 'onHoldTotalDays',
      headerName: intl.formatMessage({
        id: 'project.table.onHoldTotalDays',
        defaultMessage: 'Beklemede Gün',
      }),
      type: 'number',
      editable: false,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value !== null && value !== undefined ? value : 0),
    },
    {
      field: 'relatedContactId',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.relatedContact',
        defaultMessage: 'İlgili Kişisi',
      }),
      headerAlign: 'center',
      align: 'center',
      editable: editMode,
      valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
      renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
      minWidth: 200,
    },
    {
      field: 'alternativeRelatedContactId',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.alternativeRelatedContact',
        defaultMessage: 'Yedek İlgili Kişisi',
      }),
      headerAlign: 'center',
      align: 'center',
      editable: editMode,
      valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
      renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
      minWidth: 220,
    },
    {
      field: 'notes',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.notes',
        defaultMessage: 'Notlar',
      }),
      editable: editMode,
      minWidth: 320,
    },
    editMode
      ? {
          field: 'actions',
          type: 'actions',
          headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
          width: 100,
          cellClassName: 'actions',
          getActions: ({ id, row }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
                  sx={{
                    color: 'primary.main',
                  }}
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ];
            }

            return [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
                disabled={isCompletedProduct(row)}
                onClick={handleDeleteClick(id)}
                color="inherit"
              />,
            ];
          },
        }
      : {
          field: 'none',
        },
  ];

  // sync helper functions
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <StyledBox sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      <ZebraDataGrid
        key={editMode ? 'edit' : 'view'}
        rows={rows}
        columns={columns}
        columnVisibilityModel={{ none: false }}
        disableColumnMenu
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        isCellEditable={(params) => {
          if (!editMode) return false;
          if (params.field === 'environmentTypeId') return true;

          return !isCompletedProduct(params.row);
        }}
        localeText={renderDataGridLocaleText()}
        initialState={{
          pinnedColumns: editMode ? { right: ['actions'] } : {},
        }}
        slots={{
          toolbar: () => (
            <>
              <EditToolbarProducts
                setRowModesModel={setRowModesModel}
                setRows={setRows}
                showButton={editMode && rows.length < products.length}
              />
              {!editMode && <CustomToolbar exportPremiumExcel={true} />}
            </>
          ),
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
        hideFooter
        disableRowSelectionOnClick
        disableColumnSorting
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          border: `1px ${theme.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
        }}
        disableAggregation
        disableRowGrouping
      />
    </StyledBox>
  );
};

export default ProductsEditTable;
